// Notification preferences live entirely on the device. The settings UI in
// NotificationsSection writes to the same `chat:notifications:prefs` key —
// see that file for the authoritative shape. This module is the read-side
// helper used by the message-arrival dispatcher.

export interface NotificationGroupPrefs {
  show: boolean;
  sound: boolean;
  reactions: boolean;
}

export interface NotificationPrefs {
  message: NotificationGroupPrefs;
  group: NotificationGroupPrefs;
  call: { show: boolean; ringtone: boolean };
  preview: boolean;
  // When true, fire a desktop notification for security-sensitive events
  // (new device sign-in, password change). On by default — turn off if it's
  // noisy in a multi-device household.
  security: boolean;
}

export const NOTIFICATION_DEFAULTS: NotificationPrefs = {
  message: { show: true, sound: true, reactions: true },
  group: { show: true, sound: true, reactions: true },
  call: { show: true, ringtone: true },
  preview: true,
  security: true,
};

const KEY = 'chat:notifications:prefs';

export function loadNotificationPrefs(): NotificationPrefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return NOTIFICATION_DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<NotificationPrefs>;
    return {
      ...NOTIFICATION_DEFAULTS,
      ...parsed,
      message: { ...NOTIFICATION_DEFAULTS.message, ...(parsed.message ?? {}) },
      group: { ...NOTIFICATION_DEFAULTS.group, ...(parsed.group ?? {}) },
      call: { ...NOTIFICATION_DEFAULTS.call, ...(parsed.call ?? {}) },
    };
  } catch {
    return NOTIFICATION_DEFAULTS;
  }
}

// True when a per-conversation mute is active right now.
export function isConversationMuted(mutedUntil: string | null | undefined): boolean {
  if (!mutedUntil) return false;
  const ts = new Date(mutedUntil).getTime();
  if (Number.isNaN(ts)) return false;
  return ts > Date.now();
}
